import { ProportionChart, type ColorConfig, type ProportionSegment } from '@thegpvc/ui';
import type { TimeBreakdown } from '../types/api';
import { formatDuration as formatDurationHuman } from '../utils/format';

// Active-time palette (Dixie Session Detail spec §4). Mapped to Tailwind
// classes so the values come from the theme, not hardcoded hex.
const TIME_COLORS: Record<string, ColorConfig> = {
  thinking: { bg: 'bg-purple-500', label: 'Thinking' }, // #a855f7
  llm: { bg: 'bg-sky-500', label: 'LLM' }, //              #5b9bd5
  tool: { bg: 'bg-emerald-500', label: 'Tools' }, //       #10b981
  user: { bg: 'bg-sky-400', label: 'User' }, //            #38bdf8
  system: { bg: 'bg-navy-400', label: 'System' }, //       #8c97ad
  other: { bg: 'bg-orange-500', label: 'Other' }, //       #ff6a00
  idle: { bg: 'bg-gray-400 dark:bg-navy-600', label: 'Idle' },
};

interface TimeRibbonProps {
  breakdown: TimeBreakdown;
  height?: string;
  showLegend?: boolean;
  /** Exclude idle time so it doesn't swamp the active-time comparison. */
  excludeIdle?: boolean;
}

function breakdownToSegments(breakdown: TimeBreakdown, excludeIdle: boolean): ProportionSegment[] {
  const segments: ProportionSegment[] = [
    { key: 'thinking', value: parseInt(breakdown.thinkingMs || '0', 10) },
    { key: 'llm', value: parseInt(breakdown.llmMs || '0', 10) },
    { key: 'tool', value: parseInt(breakdown.toolMs || '0', 10) },
    { key: 'user', value: parseInt(breakdown.userMs || '0', 10) },
    { key: 'system', value: parseInt(breakdown.systemMs || '0', 10) },
    { key: 'other', value: parseInt(breakdown.otherMs || '0', 10) },
  ];
  if (!excludeIdle) {
    segments.splice(3, 0, { key: 'idle', value: parseInt(breakdown.idleMs || '0', 10) });
  }
  return segments;
}

/** Session time breakdown as a gp-ui ProportionChart. */
export function TimeRibbon({ breakdown, height = 'h-2', showLegend = false, excludeIdle = false }: TimeRibbonProps) {
  return (
    <ProportionChart
      data={breakdownToSegments(breakdown, excludeIdle)}
      colors={TIME_COLORS}
      formatValue={formatDurationHuman}
      height={height}
      showLegend={showLegend}
      ariaLabel="Session time breakdown"
    />
  );
}
